import { useApiMutation, useApiQuery } from "@/hooks/use-api";
import {
  AddTrackerRequest,
  CreateEdgeRequest,
  Investigation,
  InvestigationCreate,
  InvestigationEdge,
  InvestigationGraph,
  InvestigationUpdate,
  TrackerDocumentsResponse,
  TrackerNode,
  UpdatePositionRequest,
} from "@/models/investigation";
import { hasSelectedWorkspace } from "./core";

// ============================================================================
// Investigation CRUD (MongoDB)
// ============================================================================

/**
 * Get all investigations for the current workspace.
 */
export const useInvestigations = () =>
  useApiQuery<Investigation[]>(
    { url: "/investigations" },
    { enabled: hasSelectedWorkspace() }
  );

/**
 * Get a single investigation by ID.
 */
export const useInvestigation = (id?: string) =>
  useApiQuery<Investigation>(
    { url: `/investigations/${id}` },
    { enabled: !!id && hasSelectedWorkspace() }
  );

/**
 * Create a new investigation.
 */
export const useCreateInvestigation = () =>
  useApiMutation<Investigation, InvestigationCreate>({
    url: "investigations",
    method: "POST",
  });

/**
 * Update an existing investigation.
 */
export const useUpdateInvestigation = () =>
  useApiMutation<Investigation, InvestigationUpdate, { id: string }>({
    url: "investigations/{id}",
    method: "PATCH",
  });

/**
 * Delete an investigation.
 */
export const useDeleteInvestigation = () =>
  useApiMutation<{}, {}, { id: string }>({
    url: "investigations/{id}",
    method: "DELETE",
  });

// ============================================================================
// Investigation Graph Operations
// ============================================================================

/**
 * Get the complete graph data for an investigation.
 */
export const useInvestigationGraph = (id?: string) =>
  useApiQuery<InvestigationGraph>(
    { url: `/investigations/${id}/graph` },
    { enabled: !!id && hasSelectedWorkspace() }
  );

// ============================================================================
// Tracker Operations (Nodes)
// ============================================================================

/**
 * Add an existing tracker to the investigation.
 */
export const useAddTrackerToInvestigation = () =>
  useApiMutation<TrackerNode, AddTrackerRequest, { id: string }>({
    url: "investigations/{id}/trackers",
    method: "POST",
  });

/**
 * Remove a tracker from the investigation.
 */
export const useRemoveTrackerFromInvestigation = () =>
  useApiMutation<{ status: string }, {}, { id: string; tracker_id: string }>({
    url: "investigations/{id}/trackers/{tracker_id}",
    method: "DELETE",
  });

/**
 * Update a tracker's position in the canvas.
 */
export const useUpdateTrackerPosition = () =>
  useApiMutation<{ id: string; position: { x: number; y: number } }, UpdatePositionRequest, { id: string; tracker_id: string }>({
    url: "investigations/{id}/trackers/{tracker_id}/position",
    method: "PATCH",
  });

/**
 * Get documents from Elasticsearch for a tracker.
 */
export const useTrackerDocuments = (investigationId?: string, trackerId?: string, limit = 100, offset = 0) =>
  useApiQuery<TrackerDocumentsResponse>(
    { url: `/investigations/${investigationId}/trackers/${trackerId}/documents?limit=${limit}&offset=${offset}` },
    { enabled: !!investigationId && !!trackerId && hasSelectedWorkspace() }
  );

// ============================================================================
// Edge Operations
// ============================================================================

/**
 * Create an edge between two trackers.
 */
export const useCreateInvestigationEdge = () =>
  useApiMutation<InvestigationEdge, CreateEdgeRequest, { id: string }>({
    url: "investigations/{id}/edges",
    method: "POST",
  });

/**
 * Delete an edge from the investigation.
 */
export const useDeleteInvestigationEdge = () =>
  useApiMutation<{ status: string }, {}, { id: string; edge_id: string }>({
    url: "investigations/{id}/edges/{edge_id}",
    method: "DELETE",
  });
